import "./section-third-filters.scss";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import {
  FiltersMenuDate,
  FiltersMenuRow,
  FiltersMenuSwitch,
  FiltersMenuTags,
  JournalActiveFilterChips,
  JournalFiltersMenu,
  formatJournalFilterDate,
  type JournalActiveFilterChip,
} from "@/features/journal-filters-menu";
import { FiltersButton, InputSearchWithInfo } from "@shared/ui";

// BUILDDOCS-4959: скрыто до реализации на бэкенде
// const DISPLAY_OPTIONS = [
//   { id: "FAVORITE", label: "Избранное" },
//   { id: "ACTIVE", label: "Активное" },
//   { id: "DELETED", label: "Удаленное" },
//   { id: "ALL", label: "Все" },
// ];

const STATUS_OPTIONS = [
  { id: "NEW", label: "Новый" },
  { id: "AWAITING_SIGNATURE", label: "На подписании" },
  { id: "SIGNED", label: "Подписан" },
  { id: "SENT_TO_GSN", label: "Отправлено" },
  { id: "REJECTED", label: "Отклонено" },
];

const WORK_TYPE_OPTIONS = [
  { id: "WORK", label: "Работа" },
  { id: "TEST", label: "Испытание" },
];

type Signer = {
  representativeId?: string;
  constructionControlId?: string;
  user?: {
    userId?: string;
    surname?: string;
    firstName?: string;
    middleName?: string;
  };
};

type Draft = {
  display: string[];
  statuses: string[];
  signers: string[];
  workTypes: string[];
  actConnect: boolean;
  openRemarks: boolean;
  creationDate: string | null;
  workPeriod: [string, string];
  executionDate: string | null;
  lastChangeDate: string | null;
};

const emptyDraft = (): Draft => ({
  display: [],
  statuses: [],
  signers: [],
  workTypes: [],
  actConnect: false,
  openRemarks: false,
  creationDate: null,
  workPeriod: ["", ""],
  executionDate: null,
  lastChangeDate: null,
});

const formatSignerLabel = (signer: Signer) => {
  const surname = signer.user?.surname || "";
  const firstInitial = signer.user?.firstName ? `${signer.user.firstName[0]}.` : "";
  const middleInitial = signer.user?.middleName ? `${signer.user.middleName[0]}.` : "";
  return `${surname} ${firstInitial}${middleInitial}`.trim();
};

const getSignerId = (signer: Signer) =>
  signer.representativeId ?? signer.constructionControlId ?? signer.user?.userId ?? "";

const getWorkPeriodFromDraft = (draft: Draft): [string, string] => {
  if (draft.workPeriod[0] || draft.workPeriod[1]) {
    return draft.workPeriod;
  }
  if (draft.executionDate) {
    return [draft.executionDate, draft.executionDate];
  }
  return ["", ""];
};

export const SectionThirdFilters = ({
  onSearchChange,
  selectedStatuses,
  setSelectedStatuses,
  workTypes,
  setWorkTypes,
  signerIds,
  setSignerIds,
  actConnect,
  setActConnect,
  createDate,
  setCreateDate,
  updateDate,
  setUpdateDate,
  dateStart,
  dateEnd,
  setDateStart,
  setDateEnd,
  signersList,
  onFiltersApply,
  bulkSendMode = false,
}: {
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  workTypes: string[];
  setWorkTypes: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  actConnect: boolean;
  setActConnect: Dispatch<SetStateAction<boolean>>;
  createDate: string;
  setCreateDate: Dispatch<SetStateAction<string>>;
  updateDate: string;
  setUpdateDate: Dispatch<SetStateAction<string>>;
  dateStart: string;
  dateEnd: string;
  setDateStart: Dispatch<SetStateAction<string>>;
  setDateEnd: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFiltersApply?: () => void;
  bulkSendMode?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState<Draft>(emptyDraft);
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const hasActiveFilters =
    (!bulkSendMode && selectedStatuses.length > 0) ||
    workTypes.length > 0 ||
    signerIds.length > 0 ||
    actConnect ||
    Boolean(dateStart || dateEnd);
    // Boolean(createDate || updateDate) ||
    // applied.display.length > 0 ||
    // applied.openRemarks;

  useEffect(() => {
    setApplied((prev) => ({
      ...prev,
      statuses: selectedStatuses,
      signers: signerIds,
      workTypes,
      actConnect,
      creationDate: createDate || null,
      lastChangeDate: updateDate || null,
      workPeriod: dateStart || dateEnd ? [dateStart, dateEnd] : ["", ""],
    }));
  }, [
    selectedStatuses,
    signerIds,
    workTypes,
    actConnect,
    createDate,
    updateDate,
    dateStart,
    dateEnd,
  ]);

  const openModal = () => {
    setDraft({
      display: applied.display,
      statuses: selectedStatuses,
      signers: signerIds,
      workTypes,
      actConnect,
      openRemarks: applied.openRemarks,
      creationDate: createDate || null,
      lastChangeDate: updateDate || null,
      workPeriod: dateStart || dateEnd ? [dateStart, dateEnd] : ["", ""],
      executionDate: applied.executionDate,
    });
    setOpen(true);
  };

  const applyFilters = () => {
    const [workPeriodStart, workPeriodEnd] = getWorkPeriodFromDraft(draft);
    setApplied(draft);
    setSelectedStatuses(draft.statuses);
    setWorkTypes(draft.workTypes);
    setSignerIds(draft.signers);
    setActConnect(draft.actConnect);
    setCreateDate(draft.creationDate || "");
    setUpdateDate(draft.lastChangeDate || "");
    setDateStart(workPeriodStart);
    setDateEnd(workPeriodEnd);
    setOpen(false);
    onFiltersApply?.();
  };

  const resetFilters = () => {
    const cleared = emptyDraft();
    setDraft(cleared);
    setApplied(cleared);
    setSelectedStatuses([]);
    setWorkTypes([]);
    setSignerIds([]);
    setActConnect(false);
    setCreateDate("");
    setUpdateDate("");
    setDateStart("");
    setDateEnd("");
    setOpen(false);
    onFiltersApply?.();
  };

  return (
    <div className="section-third-filters">
      <InputSearchWithInfo
        className="section-third-filters__search"
        style={{ fontSize: "12px" }}
        onChange={(e) => onSearchChange(e.target.value)}
        tooltipTitle="Поиск по наименованию, месту"
      />
      <FiltersButton active={hasActiveFilters} onClick={openModal} />

      <JournalFiltersMenu
        open={open}
        onClose={() => setOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Отображение">
          <FiltersMenuTags
            options={DISPLAY_OPTIONS}
            value={draft.display}
            onChange={(display) => setDraft((prev) => ({ ...prev, display }))}
          />
        </FiltersMenuRow>
        */}

        {!bulkSendMode ? (
          <FiltersMenuRow
            stacked
            label="Статус записи"
            onClear={
              draft.statuses.length
                ? () => setDraft((prev) => ({ ...prev, statuses: [] }))
                : undefined
            }
          >
            <FiltersMenuTags
              options={STATUS_OPTIONS}
              value={draft.statuses}
              onChange={(statuses) => setDraft((prev) => ({ ...prev, statuses }))}
            />
          </FiltersMenuRow>
        ) : null}

        <FiltersMenuRow
          stacked
          label="Подписант"
          onClear={
            draft.signers.length
              ? () => setDraft((prev) => ({ ...prev, signers: [] }))
              : undefined
          }
        >
          <FiltersMenuTags
            options={signerOptions}
            value={draft.signers}
            onChange={(signers) => setDraft((prev) => ({ ...prev, signers }))}
          />
        </FiltersMenuRow>

        <FiltersMenuRow
          stacked
          label="Тип работы"
          onClear={
            draft.workTypes.length
              ? () => setDraft((prev) => ({ ...prev, workTypes: [] }))
              : undefined
          }
        >
          <FiltersMenuTags
            options={WORK_TYPE_OPTIONS}
            value={draft.workTypes}
            onChange={(workTypesValue) =>
              setDraft((prev) => ({ ...prev, workTypes: workTypesValue }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuSwitch
          label="Связь с исполнительной документацией"
          checked={draft.actConnect}
          onChange={(actConnectValue) =>
            setDraft((prev) => ({ ...prev, actConnect: actConnectValue }))
          }
        />

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuSwitch
          label="Открытые замечания строительного контроля"
          checked={draft.openRemarks}
          onChange={(openRemarks) => setDraft((prev) => ({ ...prev, openRemarks }))}
        />

        <FiltersMenuRow label="Дата создания">
          <FiltersMenuDate
            value={draft.creationDate}
            onChange={(creationDate) =>
              setDraft((prev) => ({
                ...prev,
                creationDate: creationDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>
        */}

        <FiltersMenuRow label="Период работ">
          <FiltersMenuDate
            range
            value={draft.workPeriod}
            onChange={(workPeriod) =>
              setDraft((prev) => ({
                ...prev,
                workPeriod: (workPeriod as [string, string]) || ["", ""],
              }))
            }
          />
        </FiltersMenuRow>

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Дата выполнения работы">
          <FiltersMenuDate
            value={draft.executionDate}
            onChange={(executionDate) =>
              setDraft((prev) => ({
                ...prev,
                executionDate: executionDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Дата последнего изменения">
          <FiltersMenuDate
            value={draft.lastChangeDate}
            onChange={(lastChangeDate) =>
              setDraft((prev) => ({
                ...prev,
                lastChangeDate: lastChangeDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>
        */}
      </JournalFiltersMenu>
    </div>
  );
};

type ActiveFilterChip = JournalActiveFilterChip;

export const SectionThirdActiveFilterChips = ({
  selectedStatuses,
  setSelectedStatuses,
  workTypes,
  setWorkTypes,
  signerIds,
  setSignerIds,
  actConnect,
  setActConnect,
  dateStart,
  dateEnd,
  setDateStart,
  setDateEnd,
  signersList,
  onFilterRemove,
  bulkSendMode = false,
}: {
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  workTypes: string[];
  setWorkTypes: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  actConnect: boolean;
  setActConnect: Dispatch<SetStateAction<boolean>>;
  dateStart: string;
  dateEnd: string;
  setDateStart: Dispatch<SetStateAction<string>>;
  setDateEnd: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFilterRemove?: () => void;
  bulkSendMode?: boolean;
}) => {
  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const removeFilter = (updater: () => void) => {
    updater();
    onFilterRemove?.();
  };

  const chips: ActiveFilterChip[] = [];

  if (!bulkSendMode) {
    selectedStatuses.forEach((statusId) => {
      const option = STATUS_OPTIONS.find((item) => item.id === statusId);
      if (!option) return;
      chips.push({
        key: `status-${statusId}`,
        filterLabel: "Статус записи",
        value: option.label,
        onRemove: () =>
          removeFilter(() =>
            setSelectedStatuses((prev) => prev.filter((item) => item !== statusId)),
          ),
      });
    });
  }

  signerIds.forEach((signerId) => {
    const option = signerOptions.find((item) => item.id === signerId);
    if (!option) return;
    chips.push({
      key: `signer-${signerId}`,
      filterLabel: "Подписант",
      value: option.label,
      onRemove: () =>
        removeFilter(() =>
          setSignerIds((prev) => prev.filter((item) => item !== signerId)),
        ),
    });
  });

  workTypes.forEach((workTypeId) => {
    const option = WORK_TYPE_OPTIONS.find((item) => item.id === workTypeId);
    if (!option) return;
    chips.push({
      key: `work-type-${workTypeId}`,
      filterLabel: "Тип работы",
      value: option.label,
      onRemove: () =>
        removeFilter(() =>
          setWorkTypes((prev) => prev.filter((item) => item !== workTypeId)),
        ),
    });
  });

  if (actConnect) {
    chips.push({
      key: "act-connect",
      filterLabel: "Связь с исполнительной документацией",
      value: "Да",
      onRemove: () => removeFilter(() => setActConnect(false)),
    });
  }

  if (dateStart || dateEnd) {
    let periodValue = "";
    if (dateStart && dateEnd) {
      periodValue = `${formatJournalFilterDate(dateStart)} — ${formatJournalFilterDate(dateEnd)}`;
    } else if (dateStart) {
      periodValue = formatJournalFilterDate(dateStart);
    } else {
      periodValue = formatJournalFilterDate(dateEnd);
    }
    chips.push({
      key: "work-period",
      filterLabel: "Период работ",
      value: periodValue,
      onRemove: () =>
        removeFilter(() => {
          setDateStart("");
          setDateEnd("");
        }),
    });
  }

  return <JournalActiveFilterChips chips={chips} />;
};
